import React from 'react';

import { Icon, type IconName } from '../Icon/Icon';
import { Root, IconWrap, Copy, Title, Body, Action, ICON_PX } from './Callout.styles';

export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

export type CalloutProps = {
  tone: CalloutTone;
  /** Body copy (English i18n key). */
  text: string;
  placeholders?: Record<string, string | number>;
  /** Optional bold first line (i18n key). */
  title?: string;
  titlePlaceholders?: Record<string, string | number>;
  /** Overrides the tone's default glyph. */
  icon?: IconName;
  /** Right-aligned on desktop, stacked below the copy on mobile (a Button, a Checkbox, a link). */
  action?: React.ReactNode;
  className?: string;
};

const DEFAULT_ICON: Record<CalloutTone, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'warning',
  danger: 'close-circle',
};

// Warnings and failures interrupt assistive tech (`alert`); confirmations and
// hints are announced politely (`status`).
const ROLE: Record<CalloutTone, 'status' | 'alert'> = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  danger: 'alert',
};

/**
 * Inline toned notice: a tinted, bordered strip with a leading glyph, an
 * optional bold title, body copy and an optional action slot. Use it for
 * persistent in-context feedback (a verification result, a forwarding hint, a
 * failed check); transient confirmations belong to `Toast`.
 *
 * Copy is translation-ready: `text` / `title` are English i18n keys rendered
 * through `Text`, with `{{placeholder}}` values supplied via the matching
 * `placeholders` / `titlePlaceholders` maps.
 */
export const Callout = ({
  tone,
  text,
  placeholders,
  title,
  titlePlaceholders,
  icon,
  action,
  className,
}: CalloutProps): React.ReactElement => (
  <Root $tone={tone} role={ROLE[tone]} className={className}>
    <IconWrap $tone={tone} aria-hidden="true">
      <Icon name={icon ?? DEFAULT_ICON[tone]} size={ICON_PX} />
    </IconWrap>
    <Copy>
      {title ? <Title text={title} placeholders={titlePlaceholders} tag="div" /> : null}
      <Body text={text} placeholders={placeholders} tag="div" />
    </Copy>
    {action ? <Action>{action}</Action> : null}
  </Root>
);

export default Callout;
